import IIntegratorFirm, {
  IntegratorFeedAuth,
} from "../interfaces/models/IIntegratorFirm";
import Axios from "../utils/axios";
import type { IntegratorRunLogAssetLink } from "./IntegratorRunLog";

const axios = new Axios();

export const getIntegratorFirms = async (): Promise<IIntegratorFirm[]> => {
  const response = await axios.Get('/integratorFirm');
  if (!Array.isArray(response.data)) {
    throw new Error("Could not load sync configurations.");
  }
  return response.data;
}

export const getIntegratorFirmByFirmId = async (firmId: string): Promise<IIntegratorFirm | undefined> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({ firmId: { value: firmId, comparisonType: 'EQUAL' } }));
    const response = await axios.Get(`/integratorFirm?${params.toString()}`);
    return response.data?.[0];
  } catch (error) {
    console.error('Error fetching integrator firm:', error);
    return undefined;
  }
}

export const createIntegratorFirm = async (integratorFirm: IIntegratorFirm): Promise<IIntegratorFirm> => {
  const response = await axios.Post('/integratorFirm', integratorFirm);
  return response.data;
}

export const updateIntegratorFirm = async (integratorFirm: IIntegratorFirm): Promise<IIntegratorFirm> => {
  const response = await axios.Put('/integratorFirm', integratorFirm);
  return response.data;
}

export const deleteIntegratorFirm = async (id: string): Promise<boolean> => {
  const response = await axios.Delete('/integratorFirm', `${id}`);
  return response.status === 200;
}

/**
 * Whether a candidate feed url can be downloaded and read, and the field and
 * collection paths present if it can. A `false` flag is a 200 outcome, not a thrown error.
 */
export interface ProbeFeedResult {
  canFetch: boolean;
  canRead: boolean;
  fields: string[];
  collections: string[];
  fromCache: boolean;
  feedCached: boolean;
  fetchError?: string;
  readError?: string;
}

export const probeIntegratorFirmFeed = async (query: {
  firmId: string;
  feedUrl: string;
  feedAuth?: IntegratorFeedAuth;
  feedTimeoutMs?: number;
  skipSslVerification?: boolean;
}): Promise<ProbeFeedResult> => {
  const response = await axios.Post(
    `/integratorFirm/${query.firmId}/probeFeed`,
    {
      feedUrl: query.feedUrl,
      feedAuth: query.feedAuth,
      feedTimeoutMs: query.feedTimeoutMs,
      skipSslVerification: query.skipSslVerification === true ? true : undefined,
    }
  );
  return response.data;
}

/** Per-professional account state, as `professionalAccounts` answers it. */
export interface ProfessionalAccountStatus {
  personId: string;
  name: string;
  email: string;
  hasAccount: boolean;
  userName?: string;
  eligible: boolean;
  reasons: string[];
}

/** One professional's outcome after `provisionAccounts` or `resendCredentials`. */
export interface CredentialEmailResult {
  personId: string;
  name: string;
  email: string;
  userId?: string;
  userName?: string;
  emailSent: boolean;
  emailError?: string;
}

/** Same cap the backend enforces on `provisionAccounts` and `resendCredentials`. */
export const MAX_PROVISION_BATCH = 100;

/** Same cap the backend enforces on `deleteProfessionals`. */
export const MAX_DELETE_BATCH = 100;

function integratorFirmActionError(error: any, fallback: string): string {
  const body = error?.response?.data;
  return (
    (typeof body === "string" && body.trim()) ||
    body?.message ||
    error?.message ||
    fallback
  );
}

export const getProfessionalAccounts = async (query: {
  firmId: string;
  personIds: string[];
}): Promise<ProfessionalAccountStatus[]> => {
  try {
    const response = await axios.Post(
      `/integratorFirm/${query.firmId}/professionalAccounts`,
      { personIds: query.personIds }
    );
    return response.data?.statuses ?? [];
  } catch (error: any) {
    throw new Error(
      integratorFirmActionError(error, "Could not load account status.")
    );
  }
};

/**
 * Creates B2C accounts for every selected professional and emails their credentials.
 * All or nothing for the accounts: a rejection means none were created. A resolved
 * promise can still carry per-person email failures (`emailSent: false`).
 */
export const provisionProfessionalAccounts = async (query: {
  firmId: string;
  personIds: string[];
}): Promise<CredentialEmailResult[]> => {
  try {
    const response = await axios.Post(
      `/integratorFirm/${query.firmId}/provisionAccounts`,
      { personIds: query.personIds }
    );
    return response.data?.results ?? [];
  } catch (error: any) {
    throw new Error(
      integratorFirmActionError(error, "Could not create the accounts.")
    );
  }
};

export const resendProfessionalCredentials = async (query: {
  firmId: string;
  personIds: string[];
}): Promise<CredentialEmailResult[]> => {
  try {
    const response = await axios.Post(
      `/integratorFirm/${query.firmId}/resendCredentials`,
      { personIds: query.personIds }
    );
    return response.data?.results ?? [];
  } catch (error: any) {
    throw new Error(
      integratorFirmActionError(error, "Could not resend the credentials.")
    );
  }
};

export type FirmProfessionalRole = "Data Admin" | "Regular User";

export interface FirmProfessionalRow {
  /** Cosmos `Person.id`; the audit log keys a professional's changes on it. */
  id: string;
  /** `Person.generatedId`. */
  rewriteId?: number;
  legacyId?: string;
  firstName: string;
  lastName: string;
  /** "Last, First". */
  name: string;
  email: string;
  office: string;
  role: FirmProfessionalRole;
  /** Searchable and with at least one practice area. */
  display: boolean;
}

export interface FirmProfessionalsReview {
  dataAdmins: FirmProfessionalRow[];
  newlyCreated: FirmProfessionalRow[];
  existing: FirmProfessionalRow[];
  suggestedDeletions: FirmProfessionalRow[];
  /** The successful run the deletion suggestions come from; null when there is none to read. */
  deletionSource: { runLogId: string; finishedDate?: number } | null;
  deletionSourceIssue?: string;
  /** 15-minute read URL for the firm's `deleted-professionals.json`; null until the first delete. */
  deletionsFile: IntegratorRunLogAssetLink | null;
}

export const getFirmProfessionalsReview = async (
  firmId: string
): Promise<FirmProfessionalsReview> => {
  try {
    const response = await axios.Post(`/integratorFirm/${firmId}/professionals`, {});
    return response.data;
  } catch (error: any) {
    throw new Error(
      integratorFirmActionError(error, "Could not load the firm's professionals.")
    );
  }
};

export interface DeletedProfessional {
  personId: string;
  name: string;
  email: string;
}

/**
 * Soft-deletes suggested deletions. All or nothing: a rejection means none were deleted.
 * Every id must still be a suggested deletion of the firm, or the request is refused.
 */
export const deleteSuggestedProfessionals = async (query: {
  firmId: string;
  personIds: string[];
}): Promise<DeletedProfessional[]> => {
  try {
    const response = await axios.Post(
      `/integratorFirm/${query.firmId}/deleteProfessionals`,
      { personIds: query.personIds }
    );
    return response.data?.results ?? [];
  } catch (error: any) {
    throw new Error(
      integratorFirmActionError(error, "No professionals were deleted.")
    );
  }
};
